import { PATHS } from './path.constant'
import { CANDIDATE_TABS, HOME_TABS, JOB_TABS } from './tab.constant'

/**
 * Maestro global search (BE: `globalSearchSuggestions` + `globalSearch` on
 * Maestro::Schemas::RecruiterSchema).
 *
 * The scope list itself is per-tenant and comes back on `scopeCounts` — nothing
 * here enumerates scopes for the UI. The maps below are presentation fallbacks
 * only, used when an older engine omits a field.
 */

// The engine returns an empty result (not an error) below this, but we avoid the
// round trip entirely.
export const GLOBAL_SEARCH_MIN_TERM_LENGTH = 2

/**
 * Type-ahead runs on the keystroke path; the full search does not.
 *
 * The API reference suggests ~250ms, which is tuned for a search that costs one
 * cheap lookup. This one costs a scope search per tab of the page — four to
 * five on Jobs, six or seven on Candidates — so a pause mid-word is an
 * expensive thing to react to. At 250ms a word like "Pharmacist" typed at a normal pace fired four
 * separate fan-outs; 400ms lands on one or two without the field feeling slow,
 * and the superseded ones are now aborted rather than run to completion.
 */
export const GLOBAL_SEARCH_SUGGESTION_DEBOUNCE = 400

// Server clamps `limit` to 10.
export const GLOBAL_SEARCH_SUGGESTION_LIMIT = 8

// Per scope when the dropdown fans out over a page's tabs — six sections of
// eight would be a fifty-row dropdown.
export const GLOBAL_SEARCH_SCOPE_SUGGESTION_LIMIT = 4

// Server clamps `first` to 100.
export const GLOBAL_SEARCH_PAGE_SIZE = 25

// What a Top Results group carries, matching the five rows the server puts in
// the groups it returns itself.
export const GLOBAL_SEARCH_GROUP_SIZE = 5

/**
 * Geometry of the header search field.
 *
 * `width` is what the field asks for; `minWidth` is what it will not go below.
 * Everything else in the header row is a sibling flex item, so nothing here has
 * to be mirrored anywhere else — the Candidates page used to keep a
 * `globalSearchHeaderClearance()` in step with these numbers to place its mode
 * toggle, which is what buried the toggle under the field whenever the two
 * drifted apart.
 */
export const GLOBAL_SEARCH_HEADER_FIELD = {
	width: 560,
	widthMd: 380,
	// Below this it stops reading as a search field, so the page title yields
	// first and the field only shrinks after that.
	minWidth: 240,
}

export const GLOBAL_SEARCH_RECENT_TERMS_LIMIT = 6

// `scope` omitted ⇒ top results. Kept as a sentinel so the sidebar and the URL
// can name that mode explicitly instead of encoding it as "no value".
export const GLOBAL_SEARCH_TOP_RESULTS = 'top_results'

export const GLOBAL_SEARCH_QUERY_PARAMS = {
	term: 'q',
	scope: 'scope',
}

// `page` groups the sidebar the way the recruiter already navigates.
export const GLOBAL_SEARCH_PAGE_ORDER = ['jobs', 'candidates', 'other']

export const GLOBAL_SEARCH_PAGE_LABELS = {
	jobs: 'GLOBAL_SEARCH_PAGE_JOBS',
	candidates: 'GLOBAL_SEARCH_PAGE_CANDIDATES',
	other: 'GLOBAL_SEARCH_PAGE_OTHER',
}

/**
 * Maestro V2 sidebar headings — the same four boards as the Jobs and
 * Candidates pages (Jobs | Submissions | Assignments | Candidates). Classic's
 * Jobs / Candidates / Other grouping stays on V1; V2 never reads it.
 */
export const GLOBAL_SEARCH_V2_PAGE_ORDER = ['jobs', 'submissions', 'assignments', 'candidates']

export const GLOBAL_SEARCH_V2_PAGE_LABELS = {
	jobs: 'GLOBAL_SEARCH_PAGE_JOBS',
	submissions: 'GLOBAL_SEARCH_PAGE_SUBMISSIONS',
	assignments: 'GLOBAL_SEARCH_PAGE_ASSIGNMENTS',
	candidates: 'GLOBAL_SEARCH_PAGE_CANDIDATES',
}

/**
 * Fallback grouping for engines older than gem 0.1.7, which do not return
 * `page` on counts/groups. Never used to decide *which* scopes exist — that is
 * always `scopeCounts` — only to decide which heading a returned scope sits
 * under. An unknown scope falls through to "other".
 */
export const GLOBAL_SEARCH_SCOPE_PAGE_FALLBACK = {
	jobs: 'jobs',
	job_active_assignments: 'jobs',
	job_submissions: 'jobs',
	job_matches: 'jobs',
	candidates: 'candidates',
	candidate_on_assignment: 'candidates',
	candidate_per_diem_eligible: 'candidates',
	candidate_active_submission: 'candidates',
	candidate_submission_ready: 'candidates',
	candidate_matches: 'candidates',
	candidate_pipeline: 'candidates',
	job_applicants: 'other',
	assignments: 'other',
	employers: 'other',
}

/**
 * Where each scope sits under V2's board tabs. `null` means the scope has no
 * V2 twin and is omitted from the V2 sidebar and Top Results — V1 still shows
 * them via the map above.
 *
 * Only the four board scopes appear. Classic tab scopes (Matches, Active
 * Assignments, On Assignment, Active Submission, Submission Ready, Applicants,
 * Pipeline, Per Diem, Recruiters) stay searchable on V1 but must not sit beside
 * the board they overlap — otherwise the sidebar reads as
 * "Submission" + "Submissions", or three kinds of Assignment at once.
 *
 * Ignores the engine's Classic `page` on purpose: a newer gem still labels
 * Assignments as "other" and Pipeline as "candidates", which is exactly what
 * V2 must not show.
 */
export const GLOBAL_SEARCH_V2_SCOPE_PAGE = {
	jobs: 'jobs',
	job_submissions: 'submissions',
	assignments: 'assignments',
	candidates: 'candidates',
	// Classic tab / Other scopes — searchable on V1, hidden on V2.
	job_matches: null,
	job_applicants: null,
	job_active_assignments: null,
	candidate_on_assignment: null,
	candidate_submission_ready: null,
	candidate_active_submission: null,
	candidate_matches: null,
	candidate_pipeline: null,
	candidate_per_diem_eligible: null,
	employers: null,
}

/**
 * Fan-out for V2 list screens — only the boards that page actually has as tabs.
 * Classic Matches / Pipeline / Submission Ready / Active Submission never appear.
 *
 *   Today:       Jobs | Submissions | Assignments | Candidates
 *   Jobs:        Jobs | Submissions | Assignments
 *   Candidates:  Candidates | Submissions | Assignments
 */
export const GLOBAL_SEARCH_V2_TODAY_SCOPES = ['jobs', 'job_submissions', 'assignments', 'candidates']

export const GLOBAL_SEARCH_V2_JOBS_SCOPES = ['jobs', 'job_submissions', 'assignments']

export const GLOBAL_SEARCH_V2_CANDIDATES_SCOPES = ['candidates', 'job_submissions', 'assignments']

/**
 * Dropdown section captions on V2 — the tab names, not the engine's Classic
 * labels ("Available Jobs", "Submission", "Submission Ready", …).
 */
export const GLOBAL_SEARCH_V2_SCOPE_LABELS = {
	jobs: 'GLOBAL_SEARCH_PAGE_JOBS',
	job_submissions: 'GLOBAL_SEARCH_PAGE_SUBMISSIONS',
	assignments: 'GLOBAL_SEARCH_PAGE_ASSIGNMENTS',
	candidates: 'GLOBAL_SEARCH_PAGE_CANDIDATES',
}

/** Scope promoted when a V2 Jobs `?view=` is on screen. */
export const GLOBAL_SEARCH_V2_JOBS_VIEW_SCOPE = {
	jobs: 'jobs',
	submissions: 'job_submissions',
	assignments: 'assignments',
	// Chip / legacy views that still mean one of the three boards.
	open: 'jobs',
	new: 'jobs',
	closed: 'jobs',
	available_jobs: 'jobs',
	'available-jobs': 'jobs',
	matches: 'jobs',
	internal_submissions: 'job_submissions',
	external_submissions: 'job_submissions',
	active_applications: 'job_submissions',
	submission: 'job_submissions',
	toas: 'assignments',
	current: 'assignments',
	ending: 'assignments',
	starting: 'assignments',
	past: 'assignments',
	all: 'assignments',
	'active-assignments': 'assignments',
	'upcoming-assignments': 'assignments',
}

export const resolveScopePageV2 = (scope) => {
	if (Object.prototype.hasOwnProperty.call(GLOBAL_SEARCH_V2_SCOPE_PAGE, scope)) {
		return GLOBAL_SEARCH_V2_SCOPE_PAGE[scope]
	}
	// Unknown scopes stay searchable but sit under Candidates rather than a
	// Classic "Other" heading V2 no longer has.
	return scope ? 'candidates' : null
}

/** True when the scope has a V2 board to live under. */
export const isV2SearchScope = (scope) => resolveScopePageV2(scope) != null

/**
 * Where a full-search record opens.
 *
 * Suggestions carry a server-built `url` and must always use it. `globalSearch`
 * records do not, so the union member decides the route. Types with no recruiter
 * screen of their own are absent on purpose — those rows render as context, not
 * as links, rather than pointing somewhere that 404s.
 */
export const GLOBAL_SEARCH_RECORD_ROUTES = {
	MaestroJob: (record) => `${PATHS.recruiter.job_info}/${record?.id}`,
	MaestroWorker: (record) => `${PATHS.recruiter.candidate_info}/${record?.id}`,
}

/**
 * Which scope the recruiter is currently standing in, keyed by route.
 *
 * Used only to decide **ordering** — the scope for the tab on screen floats to
 * the top of the sections. It never decides which scopes exist: that is
 * `scopeCounts` from the server, and which ones fan out is the tenant's
 * `global_search_top_result_scopes`.
 *
 * Tabs with no search scope of their own (Upcoming Assignments, Shift Jobs,
 * Closed Jobs, Shortlist, Dismissed) are absent on purpose, and standing on one
 * resolves to null — no promotion, and no "Current tab" badge on somebody
 * else's section. The engine has no scope for those tabs at all
 * (Maestro::GlobalSearch::Scopes::DEFINITIONS), so there is nothing to promote.
 */
const TAB_SCOPES = {
	[PATHS.recruiter.recruiter_jobs]: {
		[JOB_TABS.available_jobs]: 'jobs',
		[JOB_TABS.active_assignments]: 'job_active_assignments',
		[JOB_TABS.submission]: 'job_submissions',
		[JOB_TABS.matches]: 'job_matches',
	},
	// The dashboard's three tabs are the same views as the Candidates page's,
	// down to the statistic each one counts.
	[PATHS.recruiter.home]: {
		[HOME_TABS.submission]: 'candidate_active_submission',
		[HOME_TABS.matches]: 'candidate_matches',
		[HOME_TABS.pre_submission]: 'candidate_pipeline',
	},
	[PATHS.recruiter.candidates]: {
		[CANDIDATE_TABS.on_assignment]: 'candidate_on_assignment',
		[CANDIDATE_TABS.per_diem_eligible]: 'candidate_per_diem_eligible',
		[CANDIDATE_TABS.submission]: 'candidate_active_submission',
		[CANDIDATE_TABS.submission_ready]: 'candidate_submission_ready',
		[CANDIDATE_TABS.matches]: 'candidate_matches',
		[CANDIDATE_TABS.pre_submission]: 'candidate_pipeline',
	},
}

/**
 * The tab Maestro V2 Credentials reads its search context as, under the
 * Candidates page (see `searchContextPath`). Not a Candidates tab, and absent
 * from TAB_SCOPES on purpose, so no section is badged "Current tab" there.
 */
export const CREDENTIALS_SEARCH_TAB = 'credentials'

// No tab in the URL means the page opened on its first tab — see the
// `activeTabValue` fallback in both list features — so that is the scope the
// recruiter is actually looking at.
const PAGE_DEFAULT_SCOPES = {
	[PATHS.recruiter.recruiter_jobs]: 'job_active_assignments',
	[PATHS.recruiter.candidates]: 'candidate_on_assignment',
	[PATHS.recruiter.home]: 'candidate_active_submission',
}

/**
 * Eight tabs list a record the fan-out for their page never searches. Standing
 * on one of them and typing the name of something sitting in the list on screen
 * returned nothing at all — not "no section for your tab", but an empty
 * dropdown, because every scope in the fan-out searched a different table.
 *
 * What each of them actually queries, and the scope that searches those records:
 *
 *   Jobs › Shortlisted / Dismissed Candidates
 *     talentFavorites feedbackMatchType 'Worker'  →  MaestroWorker  →  `candidates`
 *   Candidates › Shortlisted / Dismissed Jobs
 *   Home › Shortlisted / Dismissed Jobs
 *     talentFavorites feedbackMatchType 'Job'     →  MaestroJob     →  `jobs`
 *   Jobs › Active / Upcoming Assignments
 *     workerAssignments                           →  MaestroWorkerAssignment
 *                                                                  →  `assignments`
 *
 * The last pair is the subtle one: those two tabs put a Talent column on screen
 * and their only scope, `job_active_assignments`, searches MaestroJob. A talent
 * name typed there matched nothing. `assignments` is the engine's scope for
 * exactly those rows (SearchWorkerAssignments) and no page fanned out to it.
 *
 *   Maestro V2 › Credentials (searches as Candidates › CREDENTIALS_SEARCH_TAB)
 *     searchWorkerCredentials, one row per candidate's credential
 *                                                  →  MaestroWorker  →  `candidates`
 *   Its candidates are anyone holding a credential, not the members of one
 *   Candidates tab, so without the page-wide scope a candidate outside every
 *   tab found nothing.
 *
 * These are the widest scope for the right record, not the tab's own list — a
 * shortlisted job that has since closed is not in `jobs`, and `assignments` is
 * not filtered to upcoming ones. Same kind of record and reachable beats an
 * empty dropdown. A scope per tab (`candidate_shortlist`, `job_shortlist`,
 * `job_upcoming_assignments`, ...) is the real answer, and it is one
 * Maestro::GlobalSearch::Scopes::DEFINITIONS entry each, not a frontend change.
 *
 * The remaining scope-less tabs — Shift Jobs, Closed Jobs — hold jobs on a page
 * that already searches jobs, so they need nothing here.
 *
 * This never promotes. The section is labelled "Candidates", "Available Jobs"
 * or "Assignments", which is what it is; badging it "Current tab" would be the
 * same lie this file just stopped telling.
 */
const TAB_ENTITY_FALLBACK_SCOPES = {
	[PATHS.recruiter.recruiter_jobs]: {
		shortlist: 'candidates',
		dismissed: 'candidates',
		[JOB_TABS.active_assignments]: 'assignments',
		[JOB_TABS.upcoming_assignments]: 'assignments',
	},
	[PATHS.recruiter.candidates]: {
		shortlist: 'jobs',
		dismissed: 'jobs',
		[CREDENTIALS_SEARCH_TAB]: 'candidates',
	},
	[PATHS.recruiter.home]: {
		shortlist: 'jobs',
		dismissed: 'jobs',
	},
}

/**
 * Every scope in the order the recruiter reads the tabs, left to right — the
 * single source of truth for section order across the dropdown, Top Results and
 * the sidebar. A recruiter who knows the tab bar should not have to re-learn a
 * different order in search.
 *
 * Mirrors `getTabs()` in recruiter-jobs.feature.js and
 * recruiter-candidates.feature.js. Tabs with no search scope (Upcoming
 * Assignments, Closed Jobs, Shortlist, Dismissed) are simply absent;
 * `candidates` is the page-wide scope rather than a tab, so it leads its group.
 * Scopes not listed here keep server order, after the ones that are.
 */
export const GLOBAL_SEARCH_SCOPE_ORDER = [
	'job_active_assignments',
	'jobs',
	'job_submissions',
	'job_matches',
	'candidates',
	'candidate_on_assignment',
	'candidate_per_diem_eligible',
	'candidate_active_submission',
	'candidate_submission_ready',
	'candidate_matches',
	'candidate_pipeline',
	'job_applicants',
	'assignments',
	'employers',
]

const scopeRank = (scope) => {
	const index = GLOBAL_SEARCH_SCOPE_ORDER.indexOf(scope)
	return index === -1 ? Number.MAX_SAFE_INTEGER : index
}

/** Sorts by tab order; unknown scopes fall to the end in the order given. */
export const sortByTabOrder = (entries, readScope) => [...entries].sort((a, b) => scopeRank(readScope(a)) - scopeRank(readScope(b)))

/**
 * The scopes the type-ahead fans out to, per list page, derived from the order
 * above so the two can never drift.
 *
 * The dropdown is scoped to the page you are on — that is the behaviour the old
 * universal box had, and losing it was a regression. The results page is not
 * scoped this way: it stays on the tenant's `global_search_top_result_scopes`.
 */
const PAGE_SCOPES = {
	[PATHS.recruiter.recruiter_jobs]: GLOBAL_SEARCH_SCOPE_ORDER.filter((scope) => GLOBAL_SEARCH_SCOPE_PAGE_FALLBACK[scope] === 'jobs'),
	// `candidates` is the whole page, not one of its tabs.
	[PATHS.recruiter.candidates]: GLOBAL_SEARCH_SCOPE_ORDER.filter(
		(scope) => GLOBAL_SEARCH_SCOPE_PAGE_FALLBACK[scope] === 'candidates' && scope !== 'candidates'
	),
	// The dashboard has three tabs, not seven — fanning out over the whole
	// Candidates page there would show sections it has no tab for.
	[PATHS.recruiter.home]: ['candidate_active_submission', 'candidate_matches', 'candidate_pipeline'],
}

/**
 * Ordered scopes for the page in `pathname`, the tab on screen first.
 *
 * Returns null off a list page — the dropdown then sends no `scope` and the
 * tenant's default fan-out applies, which is one request instead of six.
 */
export const resolveContextScopes = (pathname) => {
	const [, base, tab] = String(pathname || '').split('/')
	const page = `/${base || ''}`
	const scopes = PAGE_SCOPES[page]
	if (!scopes) return null

	// Appended, not promoted — see TAB_ENTITY_FALLBACK_SCOPES. It sorts into its
	// own place in tab order when the sections render.
	const fallback = TAB_ENTITY_FALLBACK_SCOPES[page]?.[tab]
	const fanOut = fallback && !scopes.includes(fallback) ? [...scopes, fallback] : scopes

	return promoteScope(fanOut, resolveContextScope(pathname), (scope) => scope)
}

/**
 * The entity-fallback scope for the tab on screen, or null.
 *
 * Separate from `resolveContextScope` on purpose. Both decide ordering, only
 * the latter decides the "Current tab" badge: this section is somebody else's
 * list of the right KIND of record, so it should be the first thing the
 * recruiter sees and must not claim to be their tab.
 *
 * Without the ordering half, the fix that added these scopes to the fan-out was
 * invisible in practice. `assignments` sorts 13th of 14 in tab order, and the
 * dropdown is a 420px scroll box with a sticky footer, so on Active Assignments
 * the section holding the rows actually on screen rendered below the fold,
 * behind "See all results" — four sections of other tabs came first.
 */
export const resolveContextEntityScope = (pathname) => {
	const [, base, tab] = String(pathname || '').split('/')
	return TAB_ENTITY_FALLBACK_SCOPES[`/${base || ''}`]?.[tab] || null
}

export const resolveContextScope = (pathname) => {
	const [, base, tab] = String(pathname || '').split('/')
	const page = `/${base || ''}`
	const tabs = TAB_SCOPES[page]
	if (!tabs) return null

	/*
	 * No tab in the URL means the page opened on its first tab, so the page
	 * default IS the tab on screen.
	 *
	 * A tab that IS in the URL but has no scope of its own — Closed Jobs, Shift
	 * Jobs, Upcoming Assignments, Shortlist, Dismissed — must resolve to null.
	 * Falling through to the page default there promoted a section the recruiter
	 * was not standing on and badged it "Current tab", which is simply untrue:
	 * on Closed Jobs the dropdown claimed Active Assignments was the current tab.
	 * Nine tabs across the three list pages were affected.
	 */
	if (!tab) return PAGE_DEFAULT_SCOPES[page] || null

	return tabs[tab] || null
}

/** Moves the entry matching `scope` to the front, leaving the rest in order. */
export const promoteScope = (entries, scope, readScope) => {
	if (!scope) return entries

	const index = entries.findIndex((entry) => readScope(entry) === scope)
	if (index <= 0) return entries

	return [entries[index], ...entries.slice(0, index), ...entries.slice(index + 1)]
}

export const resolveScopePage = (scope, page) => {
	if (page) return page
	return GLOBAL_SEARCH_SCOPE_PAGE_FALLBACK[scope] || 'other'
}

export const resolveRecordPath = (record) => {
	const builder = GLOBAL_SEARCH_RECORD_ROUTES[record?.__typename]
	return builder && record?.id ? builder(record) : null
}
